import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Row, Col, Spin, Select, DatePicker } from "antd";
import { DownloadOutlined, ClearOutlined } from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import { useFormik } from "formik";
import { langMonth, langDate, dbMonthLg, fileFormatXextension } from "../../../utils/properties";
import { buildQueryString, exportFile, showError } from "../../../utils/utilities";
import { get, toLower } from "lodash";
import dayjs from "dayjs";
import TsReport from "../../../services/api/TsReport";
import TimesheetApprovalApi from "../../../services/api/TimesheetApprovalApi";

const PlannedVsTimesheetReport = () => {
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const currentMonth = dayjs().format(dbMonthLg);
  const [state, setState] = useState({
    loading: false,
    downloadLoading: false,
    projects: [],
  });

  useEffect(() => {
    setState((prevState) => ({ ...prevState, loading: true }));
    getProjects();
    setState((prevState) => ({ ...prevState, loading: false }));
  }, []);

  const getProjects = async () => {
    const projects = [];
    const projectsData = await TsReport.getProject();
    if (!projectsData.error && projectsData.data) {
      projectsData.data.forEach((cc) => projects.push({ value: cc.id, label: cc.name }));
      setState((prevState) => ({ ...prevState, projects }));
    } else {
      showError(get(projectsData, "error.messageCode", "ER000"));
    }
  };

  const exportPlanningVsTimesheetReport = async (queryString) => {
    const exportResponse = await TimesheetApprovalApi.exportPlannedVsTimesheetReport(queryString.string);
    if (exportResponse && !exportResponse.error) {
      const downloadType = fileFormatXextension["xlsx"];
      const blob = new Blob([exportResponse], { type: downloadType });
      exportFile(blob, "planned-vs-timesheet.xlsx");
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
  };

  const formikFilter = useFormik({
    initialValues: {
      rangeMonth: [dayjs(currentMonth, dbMonthLg), dayjs(currentMonth, dbMonthLg)],
      projects: [],
    },
    enableReinitialize: true,
    validate: (values) => {
      let errors = {};
      if (!values.rangeMonth) errors.rangeMonth = "Required!";
      return errors;
    },
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, downloadLoading: true }));
      const submit = {
        projectIds: values.projects,
        minMonth: dayjs(values.rangeMonth[0], dbMonthLg).format(dbMonthLg),
        maxMonth: dayjs(values.rangeMonth[1], dbMonthLg).format(dbMonthLg),
      };
      const queryString = buildQueryString(submit, {});
      exportPlanningVsTimesheetReport(queryString);
      setState((prevState) => ({ ...prevState, downloadLoading: false }));
    },
  });

  return (
    <>
      {state.loading ? (
        <div className="container-spinner-positioning">
          <Spin spinning={state.loading} className="spinner-positioning" />
        </div>
      ) : (
        ""
      )}

      <div className="filter-container">
        <Row gutter={[48, 32]} align="bottom">
          <Col lg={8} md={8} xs={24} offset={0}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="rangeMonth">
                {t("report.filter.rangeMonth") + "*"}
              </label>
            </Row>
            <Row>
              <DatePicker.RangePicker
                picker="month"
                id="rangeMonth"
                name="rangeMonth"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                value={formikFilter.values.rangeMonth}
                onChange={(date) => formikFilter.setFieldValue("rangeMonth", date)}
              />
            </Row>
          </Col>
          <Col lg={8} md={8} xs={24} offset={0}>
            <Row justify="start">
              <label className="fw-bold mt-10" htmlFor="projects">
                {t("timesheet.report.filter.project")}
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                mode="multiple"
                className="w-100"
                id="projects"
                name="projects"
                placeholder={t("timesheet.report.filter.project")}
                onChange={(s) => {
                  formikFilter.setFieldValue("projects", s);
                }}
                onBlur={formikFilter.handleBlur}
                value={formikFilter.values.projects}
                options={state.projects}
                loading={state.loading}
                showSearch
                filterOption
                optionFilterProp="label"
              />
            </Row>
          </Col>
          <Col span={24}>
            <Row justify="end">
              <Col>
                <Button
                  type="secondary"
                  currentSize="large"
                  clickCallback={formikFilter.resetForm}
                  currentIcon={<ClearOutlined className="icon-large" />}
                />
              </Col>
              <Col className="left-margin">
                <Button
                  currentSize="large"
                  isLoading={state.downloadLoading}
                  clickCallback={formikFilter.submitForm}
                  currentIcon={<DownloadOutlined className="icon-large" />}
                  isDisabled={!formikFilter.isValid}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default PlannedVsTimesheetReport;
